import { Helmet } from '../components/Helmet';
import { Link } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import Hero from '../components/home/Hero';
import Features from '../components/home/Features';
import CourseShowcase from '../components/home/CourseShowcase';
import Testimonials from '../components/home/Testimonials';
import FAQ from '../components/home/FAQ';
import { getAllBlogPosts } from '../utils/blogUtils';

const FEATURED_ARTICLES = [
  {
    slug: 'strategie-ia-entreprise',
    title: 'Comment mettre en place une stratégie IA dans votre entreprise',
    excerpt: 'Guide pratique pour développer et déployer une stratégie IA efficace au sein de votre organisation.',
    category: 'Stratégie',
  },
  {
    slug: 'deployer-ia-entreprise-guide-complet',
    title: "Déployer l'IA en entreprise : le guide complet",
    excerpt: 'De la phase de diagnostic au déploiement opérationnel : toutes les étapes pour réussir votre projet IA.',
    category: 'Transformation',
  },
  {
    slug: 'formation-ia-catalogue-vs-sur-mesure',
    title: 'Formation IA catalogue vs sur mesure : quelle approche choisir ?',
    excerpt: 'Comparaison honnête des deux approches avec les critères de choix selon votre situation.',
    category: 'Formation IA',
  },
];

const HomePage = () => {
  const allPosts = getAllBlogPosts().filter(p => p.featured);
  const featuredArticle = allPosts.find(p => p.slug === 'ia-emploi-crise-tech-opportunite');
  const otherArticles = allPosts.filter(p => p.slug !== 'ia-emploi-crise-tech-opportunite').slice(0, 3);
  const articles = otherArticles.length >= 3 ? otherArticles : FEATURED_ARTICLES;

  return (
    <>
      <Helmet>
        <title>Upnov IA | Formations en Intelligence Artificielle pour Entreprises</title>
        <meta
          name="description"
          content="Formations professionnelles en Intelligence Artificielle pour entreprises. Développez les compétences IA de vos équipes avec des programmes sur mesure."
        />
        <meta property="og:title" content="Upnov IA | Formations en Intelligence Artificielle pour Entreprises" />
        <meta property="og:description" content="Formations professionnelles en Intelligence Artificielle pour entreprises. Développez les compétences IA de vos équipes avec des programmes sur mesure." />
        <meta property="og:url" content="https://www.upnov.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:image" content="https://www.upnov.com/homepage_share.webp" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
      </Helmet>

      <Hero />
      <Testimonials />
      <Features />
      <CourseShowcase />

      {/* Articles récents — linking interne vers le blog */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="flex items-center justify-between mb-10">
            <h2 className="heading-md">Ressources IA pour les entreprises</h2>
            <Link to="/blog/" className="text-primary font-medium flex items-center gap-1 hover:underline">
              Tous les articles <ChevronRight className="h-4 w-4" />
            </Link>
          </div>

          {/* Article featured full-width */}
          {featuredArticle && (
            <Link
              to={`/blog/${featuredArticle.slug}/`}
              className="group block mb-12 rounded-xl overflow-hidden hover:shadow-lg transition-shadow"
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                {/* Image */}
                <div className="overflow-hidden bg-gray-200">
                  <img
                    src={featuredArticle.image}
                    alt={featuredArticle.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                {/* Contenu */}
                <div className="bg-gray-50 p-8 flex flex-col justify-center">
                  <span className="text-xs font-semibold text-primary uppercase tracking-wide mb-3 block">
                    {Array.isArray(featuredArticle.category) ? featuredArticle.category[0] : featuredArticle.category}
                  </span>
                  <h3 className="font-semibold text-2xl mb-4 group-hover:text-primary transition-colors leading-snug">
                    {featuredArticle.title}
                  </h3>
                  <p className="text-gray-600 text-base mb-6">{featuredArticle.excerpt}</p>
                  <span className="text-primary text-sm font-medium inline-block">
                    Lire l'article →
                  </span>
                </div>
              </div>
            </Link>
          )}

          {/* 3 autres articles */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {articles.map((article) => (
              <Link
                key={article.slug}
                to={`/blog/${article.slug}/`}
                className="group bg-gray-50 rounded-xl p-6 hover:shadow-md transition-shadow"
              >
                <span className="text-xs font-semibold text-primary uppercase tracking-wide mb-3 block">
                  {Array.isArray(article.category) ? article.category[0] : article.category}
                </span>
                <h3 className="font-semibold text-lg mb-3 group-hover:text-primary transition-colors leading-snug">
                  {article.title}
                </h3>
                <p className="text-gray-600 text-sm line-clamp-2">{article.excerpt}</p>
                <span className="text-primary text-sm font-medium mt-4 inline-block">
                  Lire l'article →
                </span>
              </Link>
            ))}
          </div>

          <div className="mt-12 grid grid-cols-1 sm:grid-cols-3 gap-4 text-center">
            <Link to="/accompagnement-ia-entreprise/" className="bg-gray-50 rounded-xl p-5 hover:shadow-md transition-shadow">
              <p className="font-semibold mb-1">Accompagnement IA</p>
              <p className="text-gray-500 text-sm">Déploiement opérationnel et feuille de route IA</p>
            </Link>
            <Link to="/about/" className="bg-gray-50 rounded-xl p-5 hover:shadow-md transition-shadow">
              <p className="font-semibold mb-1">Qui sommes-nous ?</p>
              <p className="text-gray-500 text-sm">15 ans d'expérience Data & IA, 200+ entreprises formées</p>
            </Link>
            <Link to="/contact/" className="bg-gray-50 rounded-xl p-5 hover:shadow-md transition-shadow">
              <p className="font-semibold mb-1">Nous contacter</p>
              <p className="text-gray-500 text-sm">Réponse sous 24h — devis gratuit et sans engagement</p>
            </Link>
          </div>

          <FAQ />
        </div>
      </section>
    </>
  );
};

export default HomePage;